import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, TrendingUp, TrendingDown, DollarSign, IndianRupee } from 'lucide-react';
import { Button, Input, Select } from '../ui';
import api from '../../services/api';

interface Account {
  id: number;
  name: string;
  currency: string;
  location: string;
  account_type: string;
  account_role?: string;
  bank_name?: string;
  account_number?: string;
  holder_name?: string;
  holder_role?: string;
  opening_balance: number;
  is_active: boolean;
}

interface Transaction {
  id: number;
  transaction_date: string;
  transaction_type: string;
  from_account_id?: number;
  from_amount?: number;
  to_account_id?: number;
  to_amount?: number;
  exchange_rate?: number;
  reference?: string;
  notes?: string;
  from_account_name?: string;
  to_account_name?: string;
}

interface Balance {
  id: number;
  name: string;
  currency: string;
  location: string;
  account_type: string;
  balance: number;
  idr_equivalent: number;
  holder_name?: string;
}

const TreasuryDashboard: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'accounts' | 'transactions'>('dashboard');
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);

  const [accountForm, setAccountForm] = useState({
    name: '',
    currency: 'IDR',
    location: 'Indonesia',
    account_type: 'bank',
    account_role: 'outbound',
    bank_name: '',
    account_number: '',
    holder_name: '',
    holder_role: '',
    opening_balance: 0,
    notes: '',
  });

  const [filters, setFilters] = useState({
    currency: '',
    location: '',
    account_type: '',
    account_role: '',
  });

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'name', direction: 'asc' });

  const [transactionForm, setTransactionForm] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    transaction_type: 'transfer',
    from_account_id: '',
    from_amount: '',
    to_account_id: '',
    to_amount: '',
    exchange_rate: '',
    reference: '',
    notes: '',
  });

  const getFilteredAndSortedAccounts = () => {
    let filtered = accounts.filter(a => a.is_active);
    
    if (filters.currency) filtered = filtered.filter(a => a.currency === filters.currency);
    if (filters.location) filtered = filtered.filter(a => a.location === filters.location);
    if (filters.account_type) filtered = filtered.filter(a => a.account_type === filters.account_type);
    if (filters.account_role) filtered = filtered.filter(a => a.account_role === filters.account_role);
    
    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key as keyof Account];
      const bVal = b[sortConfig.key as keyof Account];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    
    return filtered;
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accountsRes, balancesRes, txnsRes] = await Promise.all([
        api.get('/treasury/accounts?active_only=false'),
        api.get('/treasury/balances'),
        api.get('/treasury/transactions'),
      ]);

      console.log('Accounts response:', accountsRes.data);
      console.log('Balances response:', balancesRes.data);
      console.log('Transactions response:', txnsRes.data);

      if (accountsRes.data.success) setAccounts(accountsRes.data.data);
      if (balancesRes.data.success) setBalances(balancesRes.data.data);
      if (txnsRes.data.success) setTransactions(txnsRes.data.data);
    } catch (error) {
      console.error('Error loading treasury data:', error);
      alert('Error loading treasury data: ' + (error as any).response?.data?.detail || (error as any).message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    try {
      const response = await api.post('/treasury/accounts', accountForm);
      if (response.data.success) {
        alert('Account created successfully');
        setShowAccountForm(false);
        setAccountForm({
          name: '',
          currency: 'IDR',
          location: 'Indonesia',
          account_type: 'bank',
          account_role: 'outbound',
          bank_name: '',
          account_number: '',
          holder_name: '',
          holder_role: '',
          opening_balance: 0,
          notes: '',
        });
        loadData();
      }
    } catch (error) {
      alert('Error creating account: ' + (error as any).response?.data?.detail || (error as any).message);
    }
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;
    try {
      const response = await api.put(`/treasury/accounts/${editingAccount.id}`, accountForm);
      if (response.data.success) {
        alert('Account updated successfully');
        setEditingAccount(null);
        setShowAccountForm(false);
        setAccountForm({
          name: '',
          currency: 'IDR',
          location: 'Indonesia',
          account_type: 'bank',
          account_role: 'outbound',
          bank_name: '',
          account_number: '',
          holder_name: '',
          holder_role: '',
          opening_balance: 0,
          notes: '',
        });
        loadData();
      }
    } catch (error) {
      alert('Error updating account: ' + (error as any).response?.data?.detail || (error as any).message);
    }
  };

  const handleDeleteAccount = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    try {
      const response = await api.delete(`/treasury/accounts/${id}`);
      if (response.data.success) {
        alert('Account deleted successfully');
        loadData();
      }
    } catch (error) {
      alert('Error deleting account: ' + (error as any).response?.data?.detail || (error as any).message);
    }
  };

  const handleEditClick = (acc: Account) => {
    setEditingAccount(acc);
    setAccountForm({
      name: acc.name,
      currency: acc.currency,
      location: acc.location,
      account_type: acc.account_type,
      account_role: acc.account_role || 'outbound',
      bank_name: acc.bank_name || '',
      account_number: acc.account_number || '',
      holder_name: acc.holder_name || '',
      holder_role: acc.holder_role || '',
      opening_balance: acc.opening_balance,
      notes: '',
    });
    setShowAccountForm(true);
  };

  const handleCreateTransaction = async () => {
    try {
      const response = await api.post('/treasury/transactions', {
        ...transactionForm,
        from_account_id: transactionForm.from_account_id ? parseInt(transactionForm.from_account_id) : null,
        to_account_id: transactionForm.to_account_id ? parseInt(transactionForm.to_account_id) : null,
        from_amount: transactionForm.from_amount ? parseFloat(transactionForm.from_amount) : null,
        to_amount: transactionForm.to_amount ? parseFloat(transactionForm.to_amount) : null,
        exchange_rate: transactionForm.exchange_rate ? parseFloat(transactionForm.exchange_rate) : null,
      });
      if (response.data.success) {
        alert('Transaction recorded successfully');
        setShowTransactionForm(false);
        setTransactionForm({
          transaction_date: new Date().toISOString().split('T')[0],
          transaction_type: 'transfer',
          from_account_id: '',
          from_amount: '',
          to_account_id: '',
          to_amount: '',
          exchange_rate: '',
          reference: '',
          notes: '',
        });
        loadData();
      }
    } catch (error) {
      alert('Error recording transaction');
    }
  };

  const getCurrencyIcon = (currency: string) => {
    switch (currency) {
      case 'INR':
        return <IndianRupee className="w-5 h-5" />;
      case 'USD':
        return <DollarSign className="w-5 h-5" />;
      case 'IDR':
        return <TrendingUp className="w-5 h-5" />;
      default:
        return <DollarSign className="w-5 h-5" />;
    }
  };

  const groupedBalances = balances.reduce((acc, bal) => {
    const key = `${bal.location}-${bal.currency}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(bal);
    return acc;
  }, {} as Record<string, Balance[]>);

  const summary = balances.reduce(
    (acc, bal) => {
      acc.totals[bal.currency] = (acc.totals[bal.currency] || 0) + bal.balance;
      acc.grand_total_idr += bal.idr_equivalent;
      return acc;
    },
    { totals: {} as Record<string, number>, grand_total_idr: 0 }
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="w-full px-6 py-6 flex-1 overflow-y-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">💰 Treasury Management</h1>
          <p className="text-gray-600">Track cash positions across all accounts and currencies</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 font-medium ${activeTab === 'dashboard' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('accounts')}
            className={`px-4 py-2 font-medium ${activeTab === 'accounts' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
          >
            Accounts
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`px-4 py-2 font-medium ${activeTab === 'transactions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
          >
            Transactions
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
                <div className="text-sm text-gray-600">Total INR</div>
                <div className="text-2xl font-bold text-blue-600">₹{(summary.totals['INR'] || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="text-sm text-gray-600">Total USD</div>
                <div className="text-2xl font-bold text-green-600">${(summary.totals['USD'] || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="text-sm text-gray-600">Total IDR</div>
                <div className="text-2xl font-bold text-purple-600">Rp{(summary.totals['IDR'] || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                <div className="text-sm text-gray-600">Grand Total (IDR)</div>
                <div className="text-2xl font-bold text-orange-600">Rp{summary.grand_total_idr.toLocaleString()}</div>
              </div>
            </div>

            {/* Accounts by Location & Currency */}
            {Object.entries(groupedBalances).map(([key, accs]) => {
              const [location, currency] = key.split('-');
              return (
                <div key={key} className="bg-white rounded-lg shadow p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    {getCurrencyIcon(currency)}
                    {location} - {currency}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {accs.map(acc => (
                      <div key={acc.id} className="border border-gray-200 rounded p-4 hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-gray-900">{acc.name}</div>
                            <div className="text-xs text-gray-500">{acc.account_type === 'bank' ? '🏦 Bank' : '💵 Cash'}</div>
                          </div>
                        </div>
                        {acc.holder_name && <div className="text-xs text-gray-600 mb-2">Holder: {acc.holder_name}</div>}
                        <div className="border-t pt-2 mt-2">
                          <div className="text-sm text-gray-600">Balance</div>
                          <div className="text-xl font-bold text-gray-900">
                            {currency === 'INR' ? '₹' : currency === 'USD' ? '$' : 'Rp'}
                            {acc.balance.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">≈ Rp{acc.idr_equivalent.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div>
            <div className="mb-4 flex flex-col gap-4">
              <Button onClick={() => {setEditingAccount(null); setShowAccountForm(!showAccountForm); setAccountForm({name: '', currency: 'IDR', location: 'Indonesia', account_type: 'bank', account_role: 'outbound', bank_name: '', account_number: '', holder_name: '', holder_role: '', opening_balance: 0, notes: ''});}} className="flex items-center gap-2 w-fit">
                <Plus className="w-4 h-4" />
                Add Account
              </Button>
              
              {/* Filters */}
              <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={filters.currency} onChange={(e) => setFilters({...filters, currency: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">All Currencies</option>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="IDR">IDR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <select value={filters.location} onChange={(e) => setFilters({...filters, location: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">All Locations</option>
                    <option value="India">India</option>
                    <option value="Indonesia">Indonesia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={filters.account_type} onChange={(e) => setFilters({...filters, account_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">All Types</option>
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select value={filters.account_role} onChange={(e) => setFilters({...filters, account_role: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="">All Roles</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                    <option value="both">Both</option>
                  </select>
                </div>
              </div>
            </div>

            {showAccountForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">{editingAccount ? 'Edit Account' : 'New Account'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input placeholder="Account Name" value={accountForm.name} onChange={(e) => setAccountForm({...accountForm, name: e.target.value})} />
                  <Select value={accountForm.currency} onChange={(e) => setAccountForm({...accountForm, currency: e.target.value})}>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="IDR">IDR</option>
                  </Select>
                  <Select value={accountForm.location} onChange={(e) => setAccountForm({...accountForm, location: e.target.value})}>
                    <option value="India">India</option>
                    <option value="Indonesia">Indonesia</option>
                  </Select>
                  <Select value={accountForm.account_type} onChange={(e) => setAccountForm({...accountForm, account_type: e.target.value})}>
                    <option value="bank">Bank</option>
                    <option value="cash">Cash</option>
                  </Select>
                  <Select value={accountForm.account_role} onChange={(e) => setAccountForm({...accountForm, account_role: e.target.value})}>
                    <option value="inbound">Inbound (Receive Payments)</option>
                    <option value="outbound">Outbound (Pay From)</option>
                    <option value="both">Both (Inbound & Outbound)</option>
                  </Select>
                  <Input placeholder="Bank Name" value={accountForm.bank_name} onChange={(e) => setAccountForm({...accountForm, bank_name: e.target.value})} />
                  <Input placeholder="Account Number" value={accountForm.account_number} onChange={(e) => setAccountForm({...accountForm, account_number: e.target.value})} />
                  <Input placeholder="Holder Name" value={accountForm.holder_name} onChange={(e) => setAccountForm({...accountForm, holder_name: e.target.value})} />
                  <Input placeholder="Holder Role" value={accountForm.holder_role} onChange={(e) => setAccountForm({...accountForm, holder_role: e.target.value})} />
                  <Input type="number" placeholder="Opening Balance" value={accountForm.opening_balance} onChange={(e) => setAccountForm({...accountForm, opening_balance: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button onClick={editingAccount ? handleUpdateAccount : handleCreateAccount} className="bg-green-600 hover:bg-green-700">{editingAccount ? 'Update' : 'Create'}</Button>
                  <Button onClick={() => {setShowAccountForm(false); setEditingAccount(null);}} variant="secondary">Cancel</Button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('currency')}>Currency {sortConfig.key === 'currency' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('location')}>Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('account_type')}>Type {sortConfig.key === 'account_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('account_role')}>Role {sortConfig.key === 'account_role' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Holder</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('opening_balance')}>Opening Balance {sortConfig.key === 'opening_balance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredAndSortedAccounts().map(acc => (
                    <tr key={acc.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.currency}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.location}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.account_type === 'bank' ? '🏦 Bank' : '💵 Cash'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          acc.account_role === 'inbound' 
                            ? 'bg-green-100 text-green-700' 
                            : acc.account_role === 'outbound'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {acc.account_role === 'inbound' ? '📥 Inbound' : acc.account_role === 'outbound' ? '📤 Outbound' : '🔄 Both'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.holder_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{acc.opening_balance.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button onClick={() => handleEditClick(acc)} className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                            <Edit2 className="w-4 h-4" /> Edit
                          </button>
                          <button onClick={() => handleDeleteAccount(acc.id)} className="text-red-600 hover:text-red-800 font-medium flex items-center gap-1">
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div>
            <div className="mb-4">
              <Button onClick={() => setShowTransactionForm(!showTransactionForm)} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Record Transaction
              </Button>
            </div>

            {showTransactionForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">New Transaction</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input type="date" value={transactionForm.transaction_date} onChange={(e) => setTransactionForm({...transactionForm, transaction_date: e.target.value})} />
                  <Select value={transactionForm.transaction_type} onChange={(e) => setTransactionForm({...transactionForm, transaction_type: e.target.value})}>
                    <option value="transfer">Transfer</option>
                    <option value="conversion">Conversion</option>
                    <option value="outflow">Outflow</option>
                    <option value="inflow">Inflow</option>
                  </Select>
                  {(transactionForm.transaction_type === 'transfer' || transactionForm.transaction_type === 'conversion' || transactionForm.transaction_type === 'outflow') && (
                    <>
                      <Select value={transactionForm.from_account_id} onChange={(e) => setTransactionForm({...transactionForm, from_account_id: e.target.value})}>
                        <option value="">Select From Account</option>
                        {accounts.filter(a => a.is_active).map(acc => (
                          <option key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</option>
                        ))}
                      </Select>
                      <Input type="number" placeholder="From Amount" value={transactionForm.from_amount} onChange={(e) => setTransactionForm({...transactionForm, from_amount: e.target.value})} />
                    </>
                  )}
                  {(transactionForm.transaction_type === 'transfer' || transactionForm.transaction_type === 'conversion' || transactionForm.transaction_type === 'inflow') && (
                    <>
                      <Select value={transactionForm.to_account_id} onChange={(e) => setTransactionForm({...transactionForm, to_account_id: e.target.value})}>
                        <option value="">Select To Account</option>
                        {accounts.filter(a => a.is_active).map(acc => (
                          <option key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</option>
                        ))}
                      </Select>
                      <Input type="number" placeholder="To Amount" value={transactionForm.to_amount} onChange={(e) => setTransactionForm({...transactionForm, to_amount: e.target.value})} />
                    </>
                  )}
                  {transactionForm.transaction_type === 'conversion' && (
                    <Input type="number" placeholder="Exchange Rate" value={transactionForm.exchange_rate} onChange={(e) => setTransactionForm({...transactionForm, exchange_rate: e.target.value})} />
                  )}
                  <Input placeholder="Reference" value={transactionForm.reference} onChange={(e) => setTransactionForm({...transactionForm, reference: e.target.value})} />
                  <Input placeholder="Notes" value={transactionForm.notes} onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})} />
                </div>
                <div className="mt-4 flex gap-2">
                  <Button onClick={handleCreateTransaction} className="bg-green-600 hover:bg-green-700">Record</Button>
                  <Button onClick={() => setShowTransactionForm(false)} variant="secondary">Cancel</Button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">From</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">To</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Rate</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(txn => (
                    <tr key={txn.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.transaction_date}</td>
                      <td className="px-4 py-3 text-sm"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{txn.transaction_type}</span></td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.from_account_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.to_account_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.from_amount ? txn.from_amount.toLocaleString() : '-'} → {txn.to_amount ? txn.to_amount.toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.exchange_rate ? txn.exchange_rate.toFixed(4) : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreasuryDashboard;
